import Link from "next/link";
import { ArrowRight } from "lucide-react";

import type { FeatureContent } from "@/lib/features/content";
import BasicBlock from "@/components/shared/basic-block";
import HeroBlock from "@/components/shared/hero-block";
import FancyButton from "@/components/ui/fancy-button";
import { useLocale, useStrings } from "@/lib/i18n/context";
import { getLocalizedFeature, hasTranslation } from "@/lib/i18n/content";
import { localizedPath } from "@/lib/i18n/locales";

export type MarketingStep = {
  title: string;
  description: string;
};

export type MarketingBenefit = {
  title: string;
  description: string;
};

type MarketingHeroProps = {
  prefix: string;
  highlight: string;
  suffix: string;
  description: string;
  buttonText: string;
  buttonLink: string;
  subtext?: string;
};

export const MarketingHero = ({
  prefix,
  highlight,
  suffix,
  description,
  buttonText,
  buttonLink,
  subtext,
}: MarketingHeroProps) => (
  <HeroBlock wrapperStyle="items-center gap-6 text-center lg:py-24">
    <h1 className="max-w-4xl text-3xl font-semibold leading-[1.25] text-gray-900 sm:text-4xl sm:leading-[1.25] md:text-5xl md:leading-[1.25] lg:text-6xl lg:leading-[1.25]">
      {prefix}{" "}
      <span className="inline rounded-lg bg-[#335CFF] px-[0.1em] py-0 text-white [-webkit-box-decoration-break:clone] [box-decoration-break:clone]">
        {highlight}
      </span>
      {suffix && <> {suffix}</>}
    </h1>
    <p className="max-w-2xl text-lg text-gray-700 sm:text-xl md:text-2xl">{description}</p>
    <FancyButton size="xl" href={buttonLink}>
      {buttonText}
    </FancyButton>
    {subtext && <p className="max-w-xl text-sm text-[#525866]">{subtext}</p>}
  </HeroBlock>
);

export const SectionHeader = ({ children }: { children: string }) => (
  <h2 className="text-[32px] font-semibold leading-none md:text-[40px]">{children}</h2>
);

export const HowItWorksSection = ({ steps }: { steps: MarketingStep[] }) => {
  const s = useStrings();
  return (
  <BasicBlock
    header={<SectionHeader>{s.useCase.howItWorks}</SectionHeader>}
    contentStyle="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6"
  >
    {steps.map((step, index) => (
      <div
        key={step.title}
        className="flex flex-col gap-3 rounded-2xl border border-[#EFF2FF] bg-white p-6 shadow-sm"
      >
        <span className="flex size-9 select-none items-center justify-center rounded-full bg-[#335CFF] text-base font-semibold text-white">
          {index + 1}
        </span>
        <h3 className="text-lg font-semibold text-[#101828]">{step.title}</h3>
        <p className="text-sm leading-normal text-[#525866]">{step.description}</p>
      </div>
    ))}
  </BasicBlock>
  );
};

export const BenefitsSection = ({
  header,
  benefits,
}: {
  header: string;
  benefits: MarketingBenefit[];
}) => (
  <BasicBlock
    header={<SectionHeader>{header}</SectionHeader>}
    contentStyle="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 md:gap-6"
  >
    {benefits.map((benefit) => (
      <div
        key={benefit.title}
        className="flex flex-col gap-2 rounded-2xl border border-[#EFF2FF] bg-[#F9FBFF] p-6"
      >
        <h3 className="text-base font-semibold text-[#101828]">{benefit.title}</h3>
        <p className="text-sm leading-normal text-[#525866]">{benefit.description}</p>
      </div>
    ))}
  </BasicBlock>
);

export const ToolCardsSection = ({ slugs }: { slugs: string[] }) => {
  const s = useStrings();
  const locale = useLocale();
  const tools = slugs
    .map((slug) => ({ slug, feature: getLocalizedFeature(slug, locale) }))
    .filter((tool): tool is { slug: string; feature: FeatureContent } => Boolean(tool.feature));

  if (!tools.length) return null;

  return (
    <BasicBlock
      header={<SectionHeader>{s.useCase.toolsBehind}</SectionHeader>}
      contentStyle="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-6"
    >
      {tools.map(({ slug, feature }) => (
        <Link
          key={slug}
          href={
            hasTranslation(locale, `/features/${slug}`)
              ? localizedPath(locale, `/features/${slug}`)
              : `/features/${slug}`
          }
          prefetch={false}
          className="group flex flex-col gap-2 rounded-2xl border border-[#EFF2FF] bg-white p-6 shadow-sm transition-colors hover:border-[#335CFF]"
        >
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-[#101828] transition-colors group-hover:text-[#335CFF]">
              {feature.title}
            </h3>
            <ArrowRight
              className="size-4 -translate-x-1 text-[#335CFF] opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100"
              strokeWidth={2.5}
            />
          </div>
          <p className="text-sm leading-normal text-[#525866]">{feature.description}</p>
        </Link>
      ))}
    </BasicBlock>
  );
};
